import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EnvironmentManager from '../config/EnvironmentManager';
import './ArchivedCoursesView.css';

import { 
  Search, BookOpen, Clock, Users, Award, 
  Star, Play, Bookmark, User, Archive, 
  ArchiveRestore, Edit, Trash2
} from "lucide-react";

// Navigation Bar for Archived Courses
const ArchivedNavigationBar = ({ user, onNavigate, onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <nav className="archived-nav">
      <div className="archived-nav-container">
        <div className="archived-nav-brand">
          <div className="archived-logo" onClick={() => onNavigate('home')}>
            <BookOpen size={24} />
            <span>MHMV Learning Platform</span>
          </div>
        </div>

        <div className="archived-nav-links">
          <button 
            className="archived-nav-link"
            onClick={() => onNavigate('courses')}
          >
            Courses
          </button>
          <button 
            className="archived-nav-link"
            onClick={() => onNavigate('upload')}
          >
            Upload Course
          </button>
          <button 
            className="archived-nav-link active"
            onClick={() => onNavigate('archived')}
          >
            Archived Courses
          </button>
        </div>

        <div className="archived-nav-search">
          <form onSubmit={handleSearchSubmit} className="archived-search-form">
            <div className="archived-search-wrapper">
              <Search size={18} className="archived-search-icon" />
              <input
                type="text"
                placeholder="Search archived courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="archived-search-input"
              />
            </div>
          </form>
        </div>

        <div className="archived-user-section">
          {user ? (
            <div className="archived-user-info">
              <div className="archived-user-avatar">
                {user.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="archived-user-name">
                {user.displayName || user.email?.split('@')[0]}
              </span>
            </div>
          ) : (
            <div className="archived-auth-buttons">
              <button 
                className="archived-login-btn"
                onClick={() => onNavigate('login')}
              >
                Log in
              </button>
              <button 
                className="archived-signup-btn"
                onClick={() => onNavigate('signup')}
              >
                Sign up
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// Course Actions Dropdown
const CourseActionsDropdown = ({ course, onDelete, onUnarchive, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation(); 
    setIsOpen(!isOpen);
    if (onToggle) onToggle(e);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete(course);
  };

  const handleUnarchive = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onUnarchive(course);
  };

  return (
    <div className="archived-course-actions-dropdown" ref={dropdownRef}>
      <button 
        className="archived-dropdown-toggle"
        onClick={handleToggle}
      >
        <span>•••</span>
      </button>
      
      {isOpen && (
        <div className="archived-dropdown-menu">
          <button className="archived-dropdown-item" onClick={handleUnarchive}>
            <ArchiveRestore size={14} />
            Unarchive Course
          </button>
          <button className="archived-dropdown-item archived-dropdown-item-danger" onClick={handleDelete}>
            <Trash2 size={14} />
            Delete Course
          </button>
        </div>
      )}
    </div>
  );
};

// Archived Course Card Component
const ArchivedCourseCard = ({ course, onDeleteCourse, onUnarchiveCourse }) => {
  const totalVideos = getTotalVideosCount(course);
  const totalDuration = getTotalDuration(course);

  return (
    <div className="archived-course-card">
      {/* Course Header */}
      <div className="archived-course-header">
        <div className="archived-course-badges">
          <span className="archived-archive-badge">
            <Archive size={12} />
            Archived
          </span>
          <span className="archived-level-badge">{course.level || 'ALL LEVELS'}</span>
          {course.hasCertificate && (
            <span className="archived-certificate-badge">
              <Award size={12} />
              Certificate
            </span>
          )}
        </div>
        <div className="archived-course-actions-header">
          <button className="archived-bookmark-btn">
            <Bookmark size={16} />
          </button>
          <CourseActionsDropdown 
            course={course}
            onDelete={onDeleteCourse}
            onUnarchive={onUnarchiveCourse}
          />
        </div>
      </div>

      {/* Course Content */}
      <div className="archived-course-content">
        <h3 className="archived-course-title">{course.title || 'Untitled Course'}</h3>
        <p className="archived-course-description">
          {course.description || 'No description available'}
        </p>

        <div className="archived-course-meta">
          <div className="archived-meta-item">
            <Clock size={14} />
            <span>{totalDuration}</span>
          </div>
          <div className="archived-meta-item">
            <Users size={14} />
            <span>{course.enrolledCount?.toLocaleString() || '0'} learners</span>
          </div>
        </div>

        <div className="archived-course-stats">
          <div className="archived-rating">
            <div className="archived-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  size={12} 
                  fill={star <= (course.rating || 0) ? "#f59e0b" : "none"}
                  color="#f59e0b"
                />
              ))}
            </div>
            <span className="archived-rating-text">({course.rating || 0})</span>
          </div>
          <div className="archived-category">{course.category || 'General'}</div>
        </div>
      </div>

      {/* Course Footer */}
      <div className="archived-course-footer">
        <div className="archived-course-instructor">
          <User size={14} />
          <span>By {course.instructor || 'Unknown Instructor'}</span>
        </div>
        <div className="archived-course-actions">
          <button className="archived-more-info-btn">
            More info
          </button>
          <button className="archived-start-learning-btn">
            <Play size={14} />
            Start Learning
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Archived Courses Component
const ArchivedCoursesView = () => {
  const auth = getAuth(app);
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [archivedCourses, setArchivedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [courseToUnarchive, setCourseToUnarchive] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await fetchArchivedCourses(currentUser.uid);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchArchivedCourses = async (userId) => {
    try {
      setLoading(true);
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getVideoCourseDetailsByUser}/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Filter only archived courses
      const archived = (response.data || []).filter(course => course.archiveCourse === true);
      setArchivedCourses(archived);

    } catch (error) {
      console.error('Error fetching archived courses:', error);
      setError('Failed to load archived courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getIdToken = async () => {
    const user = getAuth(app).currentUser;
    return user ? await user.getIdToken(true) : null;
  };

  const handleUnarchiveCourse = async (course) => {
    if (!course || !user) return;

    setActionLoading(true);

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        showMessage('Authentication required');
        return;
      }

      console.log('Unarchiving course:', course.courseId);

      const response = await axios.post(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.unarchiveVideoCourse}/${course.courseId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Unarchive response:', response.data);

      if (response.data.includes('unarchived successfully')) {
        setArchivedCourses(prev => prev.filter(c => c.courseId !== course.courseId));
        showMessage('Course unarchived successfully');
      }

    } catch (error) {
      console.error('Unarchive error:', error);
      showMessage('Failed to unarchive course. Please try again.');
    } finally {
      setActionLoading(false);
      setCourseToUnarchive(null);
    }
  };

  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete || !user) return;
    
    setActionLoading(true);
    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.deleteVideoCourseDetails}/${courseToDelete.courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.includes('deleted successfully')) {
        setArchivedCourses(prev => prev.filter(c => c.courseId !== courseToDelete.courseId));
        showMessage('Course deleted successfully');
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('Failed to delete course. Please try again.');
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(false);
      setCourseToDelete(null);
    }
  };

  const showMessage = (text) => {
    setError(text); 
    setTimeout(() => setError(''), 5000);
  };

  const handleNavigation = (page) => {
    switch (page) {
      case 'login':
        navigate('/MHMV-profile/jobseeker-login');
        break;
      case 'signup':
        navigate('/MHMV-profile/modal-register');
        break;
      case 'upload':
        navigate('/MHMV-profile/upload-video-course');
        break;
      case 'home':
        navigate('/MHMV-profile');
        break;
      case 'courses':
        navigate('/MHMV-profile/video-courses');
        break;
      case 'archived':
        navigate('/MHMV-profile/archived-courses');
        break;
      default:
        break;
    }
  };

  const handleSearch = (query) => {
    // Implement search functionality if needed
    console.log('Search query:', query);
  };

  if (loading) {
    return (
      <div className="archived-courses-container">
        <ArchivedNavigationBar 
          user={user}
          onNavigate={handleNavigation}
          onSearch={handleSearch}
        />
        <div className="archived-loading-state">
          <div className="archived-loading-spinner"></div>
          <p>Loading archived courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="archived-courses-container">
      <ArchivedNavigationBar 
        user={user}
        onNavigate={handleNavigation}
        onSearch={handleSearch}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="archived-modal-overlay">
          <div className="archived-confirm-modal">
            <h3>Delete Course</h3>
            <p>Are you sure you want to delete "{courseToDelete?.title}"? This action cannot be undone.</p>
            <div className="archived-modal-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="archived-cancel-btn"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteCourse}
                className="archived-delete-confirm-btn"
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unarchive Confirmation Modal */}
      {courseToUnarchive && (
        <div className="archived-modal-overlay">
          <div className="archived-confirm-modal">
            <h3>Unarchive Course</h3>
            <p>Are you sure you want to unarchive "{courseToUnarchive?.title}"?</p>
            <div className="archived-modal-actions">
              <button 
                onClick={() => setCourseToUnarchive(null)}
                className="archived-cancel-btn"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                onClick={() => handleUnarchiveCourse(courseToUnarchive)}
                className="archived-confirm-btn"
                disabled={actionLoading}
              >
                {actionLoading ? 'Unarchiving...' : 'Unarchive Course'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="archived-courses-content">
        {/* Header Section */}
        <div className="archived-courses-header">
          <div className="archived-header-info">
            <h1>Archived Courses</h1>
            <p>Your archived courses - you can unarchive them anytime</p>
          </div>
          <div className="archived-header-actions">
            <button 
              onClick={() => navigate('/MHMV-profile/video-courses')}
              className="archived-back-to-courses-btn"
            >
              ← Back to Active Courses
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="archived-error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Courses Grid */}
        {archivedCourses.length > 0 ? (
          <div className="archived-courses-grid">
            {archivedCourses.map((course, index) => (
              <ArchivedCourseCard
                key={course.courseId || index}
                course={course}
                onDeleteCourse={handleDeleteCourse}
                onUnarchiveCourse={() => setCourseToUnarchive(course)}
              />
            ))}
          </div>
        ) : (
          <div className="archived-empty-state">
            <Archive size={48} />
            <h3>No archived courses</h3>
            <p>You haven't archived any courses yet.</p>
            <button 
              onClick={() => navigate('/MHMV-profile/video-courses')}
              className="archived-back-to-courses-btn"
            >
              View Active Courses
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
const getTotalVideosCount = (course) => {
  let count = 0;
  course.modules?.forEach(module => {
    module.submodules?.forEach(submodule => {
      submodule.coursevideo?.forEach(courseVideo => {
        count += courseVideo.video?.length || 0;
      });
    });
  });
  return count;
};

const getTotalDuration = (course) => {
  const totalVideos = getTotalVideosCount(course);
  const totalMinutes = totalVideos * 30;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export default ArchivedCoursesView;